/**
 * 56. Inventory Movements — movement ledger; source document; debit/
 * credit quantity; valuation impact.
 *
 * Arabic and English (C7): every word from the catalogs; a movement's type
 * and status are the glossary's words (إضافة، صرف، تحويل مخزني…), never the
 * stored value.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { valueLabel } from "@/lib/i18n/records";
import { formatQuantity, formatMoney, formatDate } from "@/lib/money";

interface InventoryMovementRow {
  id: string;
  movementNumber: string;
  movementType:
    | "Receipt"
    | "Issue"
    | "Transfer"
    | "Adjustment"
    | "Reservation"
    | "CycleCount"
    | "Scrap";
  productSku: string;
  productName: string;
  warehouseName: string;
  quantity: string;
  unitOfMeasure: string;
  value: string;
  currencyCode: string;
  sourceDocument: string | null;
  postedAt: string | null;
  status: "Draft" | "Posted";
}

export function InventoryMovementsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<InventoryMovementRow>(
    ["inventory", "movements"],
    "/inventory/movements",
  );

  return (
    <div>
      <PageHeader
        title={t("inventory.movements.title")}
        subtitle={t("inventory.movements.subtitle")}
        actions={
          <PendingButton
            variant="primary"
            reason="stockMovement"
            slice="MF-B1b"
          >
            {t("inventory.movements.new")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("inventory.movements.list"), data.total)}>
          <DataTable<InventoryMovementRow>
            rows={data.items}
            columns={[
              {
                key: "movementNumber",
                label: t("fields.number"),
                render: (r) => (
                  <span className="atlas-mono">{r.movementNumber}</span>
                ),
              },
              {
                key: "movementType",
                label: t("fields.type"),
                render: (r) =>
                  r.movementType
                    ? valueLabel(t, "movementType", r.movementType)
                    : "—",
              },
              {
                key: "productSku",
                label: t("fields.sku"),
                render: (r) => (
                  <span className="atlas-mono">{r.productSku}</span>
                ),
              },
              { key: "warehouseName", label: t("fields.warehouse") },
              {
                key: "quantity",
                label: t("fields.quantity"),
                numeric: true,
                render: (r) => formatQuantity(r.quantity),
              },
              {
                key: "value",
                label: t("inventory.movements.value"),
                numeric: true,
                render: (r) => formatMoney(r.value, r.currencyCode),
              },
              {
                key: "sourceDocument",
                label: t("fields.source"),
                render: (r) => r.sourceDocument ?? "—",
              },
              {
                key: "postedAt",
                label: t("inventory.movements.posted"),
                render: (r) => (r.postedAt ? formatDate(r.postedAt) : "—"),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => (
                  <StatusBadge status={r.status} type="InventoryMovement" />
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
